import usePreviousProps from 'hooks/previous_props_hook'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { getSelectedTopicId } from 'reduxes/slices/selected/selectors'
import { useSelectorDelegateStore } from 'reduxes/stores/root_store/hooks'

import { getCurrentMemberId } from '../reduxes/slices/current/selectors'

export const useTypingIndicator = () => {
  const topicId = useSelector(getSelectedTopicId)
  const prevTopicId = usePreviousProps(topicId)
  const cableCar = useSelectorDelegateStore((state) => state.cablecar)
  const isTyping = useRef(false)
  const stopTypingTimerRef = useRef<any>()
  const [trixContentLoaded, setTrixContentLoaded] = useState(false)
  const currentMemberId = useSelector(getCurrentMemberId)

  useEffect(() => {
    setTrixContentLoaded(false)
  }, [topicId])

  const updateTypingStatus = useCallback(
    (topicId, status) => {
      if (!topicId) {
        return
      }
      isTyping.current = status
      try {
        cableCar.perform('typing', {
          data: {
            typing: status,
            topicId: topicId,
            memberId: currentMemberId,
          },
        })
      } catch {
        null
      }
    },
    [cableCar, currentMemberId],
  )

  const handleTypingStopped = useCallback(
    (topicId) => {
      if (!isTyping.current) {
        return
      }
      updateTypingStatus(topicId, false)
      stopTypingTimerRef.current = undefined
    },
    [updateTypingStatus],
  )

  const clearIndicator = useCallback(
    (topicId) => {
      if (stopTypingTimerRef.current) {
        window.clearTimeout(stopTypingTimerRef.current)
      }
      handleTypingStopped(topicId)
    },
    [handleTypingStopped],
  )

  useEffect(() => {
    if (topicId !== prevTopicId) {
      clearIndicator(prevTopicId)
    }
  }, [clearIndicator, prevTopicId, topicId])

  const handleTyping = useCallback(
    (html: string) => {
      if (!trixContentLoaded) {
        // do not want the typing indicator showing when navigating to a topic with a saved message in the editor
        setTrixContentLoaded(true)
        return
      }
      if (html !== '') {
        if (!isTyping.current) {
          updateTypingStatus(topicId, true)
        }
        if (stopTypingTimerRef.current) {
          window.clearTimeout(stopTypingTimerRef.current)
        }
        stopTypingTimerRef.current = window.setTimeout(() => {
          handleTypingStopped(topicId)
        }, 5000)
      } else {
        clearIndicator(topicId)
      }
    },
    [
      clearIndicator,
      handleTypingStopped,
      topicId,
      trixContentLoaded,
      updateTypingStatus,
    ],
  )

  return {
    handleTyping,
    clearIndicator,
  }
}
